import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import BackupService from '@/services/backup'
import {
  getActiveEditorChapter,
  subscribeChapterPersistence,
} from '@/services/chapterPersistenceOrchestrator'
import {
  hasVisibleSyncActivity,
  resolveSyncPhase,
  type ChapterSyncPhase,
} from '@/utils/chapterSyncPhase'

export type ChapterSyncItem = {
  projectId: string
  chapterId: string
  title: string
  phase: ChapterSyncPhase
  message?: string
}

let unsubscribePersistence: (() => void) | null = null

function resolveItemMessage(
  phase: ChapterSyncPhase,
  backupMessage?: string,
): string | undefined {
  if (phase === 'offline') {
    return backupMessage || 'No hay conexión para subir este capítulo.'
  }
  if (phase === 'auth_blocked') {
    return backupMessage || 'La sesión expiró. Vuelve a entrar para subir este capítulo.'
  }
  return backupMessage
}

export const useChapterSyncStore = defineStore('chapterSync', () => {
  const items = ref<ChapterSyncItem[]>([])

  const refresh = () => {
    const next: ChapterSyncItem[] = []
    const activeEditor = getActiveEditorChapter()
    for (const backup of BackupService.listChapterBackups()) {
      const phase = resolveSyncPhase(backup)
      if (!hasVisibleSyncActivity(phase)) continue
      if (
        activeEditor
        && backup.projectId === activeEditor.projectId
        && backup.chapterId === activeEditor.chapterId
      ) {
        continue
      }
      next.push({
        projectId: backup.projectId,
        chapterId: backup.chapterId,
        title: backup.title || 'Capítulo sin título',
        phase,
        message: resolveItemMessage(phase, backup.lastErrorMessage),
      })
    }
    items.value = next
  }

  const ensureSubscribed = () => {
    if (unsubscribePersistence) return
    unsubscribePersistence = subscribeChapterPersistence(() => {
      refresh()
    })
    refresh()
  }

  const hasVisibleActivity = computed(() => items.value.length > 0)

  const uploadingCount = computed(
    () => items.value.filter((item) => item.phase === 'uploading' || item.phase === 'dirty').length,
  )

  return {
    items,
    hasVisibleActivity,
    uploadingCount,
    refresh,
    ensureSubscribed,
  }
})
